class KohinoorNavigation {
  constructor(root) {
    this.root = root;
    this.drawer = document.querySelector('[data-mobile-drawer]');
    this.openButton = root.querySelector('[data-drawer-open]');
    this.closeButtons = this.drawer?.querySelectorAll('[data-drawer-close]') || [];
    this.menuPanels = this.drawer?.querySelectorAll('[data-menu-panel]') || [];
    this.panelTriggers = this.drawer?.querySelectorAll('[data-menu-target]') || [];
    this.panelBackButtons = this.drawer?.querySelectorAll('[data-menu-back]') || [];
    this.lastFocusedElement = null;
    this.panelHistory = [];
    this.inertElements = [];
    this.closeTimer = null;
    this.desktopQuery = window.matchMedia('(min-width: 1100px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.closeTimers = new WeakMap();

    this.bindDrawer();
    this.bindDesktopMenus();
  }

  bindDrawer() {
    if (!this.drawer || !this.openButton) return;

    this.openButton.addEventListener('click', () => {
      if (this.drawer.open) return;

      this.lastFocusedElement = document.activeElement;
      window.clearTimeout(this.closeTimer);
      this.drawer.dataset.state = 'closed';
      this.panelHistory = [];
      this.showMobilePanel('root', { moveFocus: false });

      if (typeof this.drawer.showModal === 'function') {
        this.drawer.showModal();
      } else {
        this.drawer.setAttribute('open', '');
      }

      this.setPageInert(true);
      this.openButton.setAttribute('aria-expanded', 'true');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.drawer.dataset.state = 'open';
          this.focusFirstDrawerControl();
        });
      });
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer());
    });

    this.panelTriggers.forEach((button) => {
      button.addEventListener('click', () => {
        const activePanel = Array.from(this.menuPanels).find((panel) => panel.dataset.active === 'true');
        this.panelHistory.push({
          panelName: activePanel?.dataset.menuPanel || 'root',
          trigger: button,
        });
        this.showMobilePanel(button.dataset.menuTarget);
      });
    });

    this.panelBackButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const previousPanel = this.panelHistory.pop() || { panelName: 'root', trigger: null };
        this.showMobilePanel(previousPanel.panelName, { focusTarget: previousPanel.trigger });
      });
    });

    this.drawer.addEventListener('click', (event) => {
      if (event.target === this.drawer) {
        this.closeDrawer();
      }
    });

    this.drawer.addEventListener('close', () => {
      this.drawer.dataset.state = 'closed';
      this.openButton.setAttribute('aria-expanded', 'false');
      this.showMobilePanel('root', { moveFocus: false });
      this.setPageInert(false);
      this.restoreFocus();
    });

    this.drawer.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDrawer();
    });

    this.drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') {
        this.trapDrawerFocus(event);
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
      }
    });
  }

  bindDesktopMenus() {
    const menus = this.root.querySelectorAll('.kh-nav-item--mega');

    const openMenu = (targetMenu) => {
      if (!this.desktopQuery.matches) return;

      const timer = this.closeTimers.get(targetMenu);
      if (timer) window.clearTimeout(timer);

      menus.forEach((menu) => {
        if (menu !== targetMenu) {
          menu.removeAttribute('open');
        }
      });

      targetMenu.setAttribute('open', '');
    };

    const closeMenu = (targetMenu) => {
      const timer = window.setTimeout(() => {
        if (!targetMenu.matches(':hover') && !targetMenu.contains(document.activeElement)) {
          targetMenu.removeAttribute('open');
        }
      }, 120);

      this.closeTimers.set(targetMenu, timer);
    };

    menus.forEach((menu) => {
      const summary = menu.querySelector('summary');

      menu.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'touch') {
          openMenu(menu);
        }
      });

      menu.addEventListener('pointerleave', () => {
        closeMenu(menu);
      });

      menu.addEventListener('focusin', () => {
        openMenu(menu);
      });

      menu.addEventListener('focusout', () => {
        closeMenu(menu);
      });

      if (summary) {
        summary.addEventListener('click', (event) => {
          if (!this.desktopQuery.matches) return;
          event.preventDefault();
          openMenu(menu);
        });
      }

      menu.addEventListener('toggle', () => {
        if (!menu.open) return;

        menus.forEach((otherMenu) => {
          if (otherMenu !== menu) {
            otherMenu.removeAttribute('open');
          }
        });
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          menu.removeAttribute('open');
          const summary = menu.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    });

    document.addEventListener('click', (event) => {
      menus.forEach((menu) => {
        if (menu.open && !menu.contains(event.target)) {
          menu.removeAttribute('open');
        }
      });
    });

    this.desktopQuery.addEventListener('change', (event) => {
      if (!event.matches) {
        menus.forEach((menu) => menu.removeAttribute('open'));
      }
    });
  }

  closeDrawer() {
    if (!this.drawer || !this.drawer.open || this.drawer.dataset.state === 'closing') return;

    this.drawer.dataset.state = 'closing';
    this.openButton.setAttribute('aria-expanded', 'false');

    const finishClose = () => {
      if (typeof this.drawer.close === 'function' && this.drawer.open) {
        this.drawer.close();
        return;
      }

      this.drawer.removeAttribute('open');
      this.drawer.dataset.state = 'closed';
      this.showMobilePanel('root', { moveFocus: false });
      this.setPageInert(false);
      this.restoreFocus();
    };

    if (this.reducedMotionQuery.matches) {
      finishClose();
      return;
    }

    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(finishClose, 320);
  }

  showMobilePanel(panelName, { moveFocus = true, focusTarget = null } = {}) {
    const targetPanel = Array.from(this.menuPanels).find((panel) => panel.dataset.menuPanel === panelName);
    if (!targetPanel) return;

    this.menuPanels.forEach((panel) => {
      const isTarget = panel === targetPanel;
      const isBehind =
        !isTarget &&
        this.panelHistory.some((entry) => entry.panelName === panel.dataset.menuPanel);

      panel.classList.toggle('is-active', isTarget);
      panel.classList.toggle('is-behind', isBehind);
      panel.dataset.active = String(isTarget);
      panel.setAttribute('aria-hidden', String(!isTarget));
      panel.inert = !isTarget;

      if (isTarget) panel.scrollTop = 0;
    });

    if (!moveFocus) return;

    const nextFocusTarget =
      focusTarget || (panelName === 'root'
        ? targetPanel.querySelector('[data-menu-target], a, button')
        : targetPanel.querySelector('[data-menu-back]'));

    window.requestAnimationFrame(() => {
      if (nextFocusTarget) nextFocusTarget.focus({ preventScroll: true });
    });
  }

  setPageInert(isInert) {
    if (isInert) {
      this.inertElements = Array.from(document.body.children).filter((element) => element !== this.drawer && !element.contains(this.drawer));
      this.inertElements.forEach((element) => {
        element.inert = true;
      });
      return;
    }

    this.inertElements.forEach((element) => {
      element.inert = false;
    });
    this.inertElements = [];
  }

  focusFirstDrawerControl() {
    const firstControl = this.drawer.querySelector('[data-drawer-close]');

    if (firstControl) {
      firstControl.focus();
    }
  }

  trapDrawerFocus(event) {
    const focusableElements = Array.from(
      this.drawer.querySelectorAll(
        'button, [href], input, select, textarea, summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (element) =>
        !element.disabled &&
        element.offsetParent !== null &&
        !element.closest('[inert]')
    );

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  restoreFocus() {
    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-navigation-section]').forEach((section) => {
    new KohinoorNavigation(section);
  });
});
